import { cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import { Check, Plus, X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	IconButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { FolderColorIcon } from "src/components/Folders/FolderColorIcon";
import type { Folder, FolderDetails } from "src/components/Folders/types";
import { FolderModal } from "src/components/Modals/FolderModal";
import { useLocalSearch } from "src/hooks/useLocalSearch";
import SearchIcon from "src/images/Icons/newIcons/search.svg";

interface FolderPickerListProps {
	folders: Folder[];
	isLoading: boolean;
	selectedFolderIds: string[];
	onToggle: (id: string) => void;
	showCreateModal: boolean;
	isCreatingFolder: boolean;
	onOpenCreateModal: () => void;
	onCloseCreateModal: () => void;
	onCreateFolder: (
		name: string,
		details: FolderDetails | null,
	) => Promise<void>;
	folderTestId?: string;
	createButtonTestId?: string;
	onClearAll?: () => void;
	/** Lewa strona paska nad lista (domyslnie licznik zaznaczonych). */
	summary?: ReactNode;
}

export function FolderPickerList({
	folders,
	isLoading,
	selectedFolderIds,
	onToggle,
	showCreateModal,
	isCreatingFolder,
	onOpenCreateModal,
	onCloseCreateModal,
	onCreateFolder,
	folderTestId = "folder-picker-item",
	createButtonTestId = "folder-picker-create-button",
	onClearAll,
	summary,
}: FolderPickerListProps) {
	const { t } = useTranslation();
	const { searchValue, setSearchValue, filteredValues, trimmedSearch } =
		useLocalSearch(folders, ["name"]);

	if (showCreateModal) {
		return (
			<FolderModal
				onClose={onCloseCreateModal}
				onSubmit={onCreateFolder}
				isLoading={isCreatingFolder}
				title={t("modals.createFolder.title", "Utwórz nowy folder")}
				submitLabel={t("modals.createFolder.submit", "Utwórz folder")}
			/>
		);
	}

	const renderList = () => {
		if (isLoading) {
			return Array.from({ length: 4 }).map((_, i) => (
				<Skeleton
					key={i}
					variant="rounded"
					height={60}
					className="border-radius-16"
				/>
			));
		}
		if (filteredValues.length === 0) {
			return (
				<span className="text-grey size-14 text-center py-24">
					{trimmedSearch
						? t("folder.noSearchResults", "Brak wyników wyszukiwania")
						: t("folder.noOtherFolders", "Brak innych folderów")}
				</span>
			);
		}
		return filteredValues.map((folder) => {
			const isSelected = selectedFolderIds.includes(String(folder.id));
			return (
				<button
					type="button"
					key={folder.id}
					role="checkbox"
					aria-checked={isSelected}
					data-test-id={folderTestId}
					data-folder-id={folder.id}
					onClick={() => onToggle(String(folder.id))}
					className={cx(
						"button-reset flex items-center gap-12 w-100 text-left py-8 px-12 border-medium-grey border-radius-16 bg-white selectable-hover",
						isSelected && "item-selected",
					)}
				>
					<FolderColorIcon
						color={folder.details?.color}
						iconClassName="icon-small"
						className="square-36"
					/>
					<span className="flex flex-col flex-1 min-w-0">
						<span className="size-16 text-black text-ellipsis">
							{folder.name}
						</span>
						<span className="size-14-light text-grey">
							{t("folder.itemsCount", "{{count}} elementów", {
								count: folder.totalItemsCount ?? 0,
							})}
						</span>
					</span>
					<span className="radio-indicator">
						{isSelected && <Check className="icon-super-small" />}
					</span>
				</button>
			);
		});
	};

	return (
		<div className="flex flex-col w-100 mt-8 gap-16 flex-1 min-h-0">
			<div className="flex items-center justify-between gap-12">
				{summary ?? (
					<div className="flex items-center gap-4">
						<span className="size-14 text-black-80">
							{t("folder.selectedCount", "Zaznaczono: {{count}}", {
								count: selectedFolderIds.length,
							})}
						</span>
						{selectedFolderIds.length > 0 && onClearAll && (
							<IconButton
								onClick={onClearAll}
								data-test-id="folder-picker-clear-button"
							>
								<X className="icon-super-small" />
							</IconButton>
						)}
					</div>
				)}
				<TextPrimaryButton
					data-test-id={createButtonTestId}
					onClick={onOpenCreateModal}
					className="flex items-center gap-4 w-fit-content"
				>
					<Plus className="icon-super-small" />
					{t("foldersList.newFolder", "Nowy folder")}
				</TextPrimaryButton>
			</div>
			<TextFieldGrey
				variant="filled"
				placeholder={t("folder.searchFolder", "Szukaj folderu")}
				value={searchValue}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
					setSearchValue(e.target.value)
				}
				onClear={() => setSearchValue("")}
				className="w-100"
				slotProps={{
					htmlInput: {
						"data-test-id": "folder-picker-search-input",
						"aria-label": t("folder.searchFolder", "Szukaj folderu"),
					},
					input: {
						disableUnderline: true,
						startAdornment: (
							<InputAdornment position="start" className="m-0 mr-8">
								<SearchIcon className="icon-normal icon-black-80" />
							</InputAdornment>
						),
					},
				}}
			/>
			<div className="flex flex-col gap-8 pr-4 flex-1 min-h-0 ofy-auto">
				{renderList()}
			</div>
		</div>
	);
}
